import type { BodyHandle } from 'box3d-web';
import {
  FRACTURE_RNG_SEED,
  mulberry32,
  subdivideCell,
  type FractureCell,
  type Vec3,
} from '../../services/fracture/voronoiFracture';
import {
  BLAST_MAX_LAUNCH,
  launchVelocityFromImpulse,
  resolveDamageCore,
  type DamageEvent,
  type ImpulsePlan,
  type ResolveResult,
} from '../../services/damageModel';
import { applyComminution } from '../../services/comminution';
import { decideFirstFractureCommit } from '../../services/impactEvaluation';
import { subFractureBudgetForEvent } from './demolitionPhysicsConstants';
import {
  COLLAPSE_DELAY_MAX,
  COLLAPSE_DELAY_MIN,
  FRAMED_BOULDER_KEEP_Y,
  FRAMED_BOULDER_LATERAL_M,
  GROUND_EPS,
  MAX_DYNAMIC_CHUNKS,
  MAX_LAUNCH_SPEED,
  PRECRACK_PIECES,
  SUBSEQUENT_IMPACT_SUB_BUDGET,
} from './demolitionTuning';
import { destroyOwnedBody } from './nativeOwnership';
import { recordDamageFact } from './impactPipeline';
import { publishLastResolve } from './telemetry';
import {
  beginCarveComminution,
  commitCarveComminution,
  comminutionContextForEvent,
  refreshFragmentVolumes,
  trySubdivideForCarve,
} from './comminutionBridge';
import {
  clampLaunchSpeeds,
  countDynamics,
  queueShardFromCell,
  queueShardFromCellVel,
  rebuildBuffers,
  recountCells,
  releasePositionsFromSamples,
  spawnDynamicRespectingCap,
  unembedSpikesNearReleased,
} from './debrisPool';
import {
  buildImpactPartition,
  commitLatentPartition,
  liveCellById,
  noteLiveCapHit,
  removeCellFromLiveRegistry,
  setLiveCells,
  spawnCellBody,
  worldCentroid,
  wouldExceedLiveCap,
} from './wallSetup';
import type {
  CarveOutcome,
  CellRecord,
  DemolitionImpact,
  DemolitionRuntime,
  ImpactReleaseSample,
  ResolveDamageOptions,
} from './types';

// ---------------------------------------------------------------------------
// Fracture / carve (D2.2 physical resolver)
// ---------------------------------------------------------------------------

/**
 * Build resolver candidates from static live cells (or pristine snapshot for first fracture).
 */
export function buildResolveCandidates(
  rt: DemolitionRuntime,
  cells: FractureCell[],
): import('../../services/damageModel').ResolveCandidate[] {
  const out: import('../../services/damageModel').ResolveCandidate[] = [];
  const usePristineFatigue = !rt.fractured;
  for (const cell of cells) {
    const rec = rt.records.get(cell.id);
    // First fracture: no records yet — use world centroid from wall center.
    const centroid = rec?.worldCentroid ?? worldCentroid(rt, cell);
    const mass = rec?.mass ?? rt.profile.density * cell.volume;
    const damage =
      rec?.damage ??
      (usePristineFatigue ? (rt.pristineDamage.get(cell.id) ?? 0) : 0);
    const neighbors = rt.adjacency.get(cell.id) ?? cell.neighbors ?? [];
    out.push({
      id: cell.id,
      centroid,
      volume: cell.volume,
      damage,
      mass: Math.max(mass, 0.05),
      neighbors: neighbors.slice(),
    });
  }
  return out;
}

/**
 * Resolve a DamageEvent against the runtime's static cells / pristine partition.
 * Pure plan — does not mutate the world. Application is firstFracture/subsequentCarve.
 */

export function resolveDamage(
  rt: DemolitionRuntime,
  event: DamageEvent,
  options: ResolveDamageOptions = {},
): ResolveResult {
  const isFirst = !rt.fractured;
  let cells: FractureCell[];
  if (options.cells) {
    cells = options.cells;
  } else if (isFirst) {
    // Impact-timed glass partition may not exist yet — caller builds it first.
    cells = rt.pristineCells.length > 0 ? rt.pristineCells.slice() : rt.cells.slice();
  } else {
    cells = [];
    for (const cell of rt.cells) {
      const rec = rt.records.get(cell.id);
      if (rec && rec.state === 'static') cells.push(cell);
    }
  }

  const candidates = buildResolveCandidates(rt, cells);
  const existingDamage = new Map<number, number>();
  for (const c of candidates) existingDamage.set(c.id, c.damage);

  const defaultSubBudget = subFractureBudgetForEvent(
    event.footprint.kind,
    rt.profile.subFractureBudget,
    rt.volleySubBudget,
  );

  return resolveDamageCore({
    physics: rt.profile.physics,
    candidates,
    event,
    subBudget: options.subBudget ?? defaultSubBudget,
    upwardBias: rt.profile.impulse.upwardBias,
    rng: rt.subRng,
    existingDamage,
    preferredPrimaryCellId: options.preferredPrimaryCellId,
    launchScale: options.launchScale,
  });
}

/**
 * Nearest Voronoi *seed* to a wall-local contact point.
 * Uses cell.seed (the site that owns the cell); falls back to centroid only
 * when seed is absent so the preferred primary actually selects the
 * containing Voronoi cell rather than a mass-centroid neighbor.
 */
export function nearestCellSeedId(
  cells: Array<{ id: number; seed?: Vec3; centroid: Vec3 }>,
  local: Vec3,
): number | undefined {
  if (cells.length === 0) return undefined;
  let bestId = cells[0].id;
  let bestDist = Infinity;
  for (const cell of cells) {
    const site = cell.seed ?? cell.centroid;
    const d = Math.hypot(
      site.x - local.x,
      site.y - local.y,
      site.z - local.z,
    );
    if (d < bestDist || (d === bestDist && cell.id < bestId)) {
      bestDist = d;
      bestId = cell.id;
    }
  }
  return bestId;
}

export function emptyCarveOutcome(
  damageOrigin: { x: number; y: number; z: number },
  appliedEnergy: number,
  preferredPrimaryCellId?: number,
): CarveOutcome {
  return {
    topologyChanged: false,
    carvedCellIds: [],
    preferredPrimaryCellId,
    damageOrigin: { ...damageOrigin },
    appliedEnergy,
    directReleases: [],
    supportReleases: [],
  };
}

export function clampDamage(value: number): number {
  return Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
}

export function damageBeforeAt(
  rt: DemolitionRuntime,
  impact: DemolitionImpact,
  preferredPrimaryCellId?: number,
): number {
  if (preferredPrimaryCellId !== undefined) {
    const record = rt.records.get(preferredPrimaryCellId);
    if (record) return clampDamage(record.damage);
    return clampDamage(rt.pristineDamage.get(preferredPrimaryCellId) ?? 0);
  }

  const local: Vec3 = {
    x: impact.x - rt.wallCenter.x,
    y: impact.y - rt.wallCenter.y,
    z: impact.z - rt.wallCenter.z,
  };
  const cells = rt.pristineCells.length > 0 ? rt.pristineCells : rt.cells;
  const nearest = nearestCellSeedId(cells, local);
  if (nearest === undefined) return 0;
  const record = rt.records.get(nearest);
  if (record) return clampDamage(record.damage);
  return clampDamage(rt.pristineDamage.get(nearest) ?? 0);
}

export function resolvedSeverity(rt: DemolitionRuntime, event: DamageEvent, result: ResolveResult | null): number {
  let depositSeverity = 0;
  if (result) {
    for (const deposit of result.deposits) {
      if (deposit.thresholdE > 0) {
        depositSeverity = Math.max(depositSeverity, deposit.energy / deposit.thresholdE);
      }
    }
  }
  const energyScale = event.footprint.kind === 'sphere'
    ? rt.profile.physics.fractureEnergy * 90
    : rt.profile.physics.fractureEnergy * 24;
  const energySeverity = event.energy / Math.max(energyScale, 1);
  const volumeSeverity = result?.carvedVolumeFrac ?? 0;
  return clampDamage(Math.max(depositSeverity, energySeverity, volumeSeverity * 2.4));
}

export function releasesFromCellIds(
  rt: DemolitionRuntime,
  cellIds: number[],
  cellLookup: FractureCell[],
  impulseByCell?: ReadonlyMap<number, ImpulsePlan>,
): ImpactReleaseSample[] {
  const byId = new Map(cellLookup.map((c) => [c.id, c]));
  const out: ImpactReleaseSample[] = [];
  for (const id of cellIds) {
    const launch = launchVelocityFromImpulse(impulseByCell?.get(id));
    const rec = rt.records.get(id);
    if (rec) {
      out.push({
        id,
        position: {
          x: rec.worldCentroid.x,
          y: rec.worldCentroid.y,
          z: rec.worldCentroid.z,
        },
        ...launch,
      });
      continue;
    }
    const cell = byId.get(id) ?? rt.pristineCellById.get(id);
    if (cell) {
      out.push({ id, position: worldCentroid(rt, cell), ...launch });
    }
  }
  return out;
}

/** Support releases newly scheduled on the collapse queue since `beforeIds`. */
export function supportReleasesSince(
  rt: DemolitionRuntime,
  beforeIds: Set<number>,
  cellLookup: FractureCell[],
  impulseByCell?: ReadonlyMap<number, ImpulsePlan>,
): ImpactReleaseSample[] {
  const added: number[] = [];
  for (const job of rt.collapseQueue) {
    if (!beforeIds.has(job.cellId)) added.push(job.cellId);
  }
  return releasesFromCellIds(rt, added, cellLookup, impulseByCell);
}

/** Deterministic stagger in [COLLAPSE_DELAY_MIN, COLLAPSE_DELAY_MAX]. */
export function collapseDelayForCell(cellId: number, wallVersion: number, index: number): number {
  // xorshift-ish mix; stable across runs (no Math.random).
  let h = (Math.imul(cellId | 0, 0x9e3779b1) ^ Math.imul(wallVersion | 0, 0x85ebca6b)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b) >>> 0;
  h ^= h >>> 16;
  const u = (h >>> 0) / 0x1_0000_0000;
  return (
    COLLAPSE_DELAY_MIN +
    u * (COLLAPSE_DELAY_MAX - COLLAPSE_DELAY_MIN) +
    index * 0.012
  );
}

export function reseedSubRng(rt: DemolitionRuntime, impact: DemolitionImpact) {
  // Deterministic per impact point + speed so e2e is stable.
  const sx = Math.floor(impact.x * 1000);
  const sy = Math.floor(impact.y * 1000);
  const sz = Math.floor(impact.z * 1000);
  const sp = Math.floor(impact.speed * 10);
  const base =
    'seed' in rt.profile.seedSpec ? rt.profile.seedSpec.seed : FRACTURE_RNG_SEED;
  const seed = (base ^ (sx * 73856093) ^ (sy * 19349663) ^ (sz * 83492791) ^ sp) >>> 0;
  rt.subRng = mulberry32(seed);
}

export function applyImpulseToBody(
  rt: DemolitionRuntime,
  bodyId: BodyHandle,
  plan: { vx: number; vy: number; vz: number },
  maxSpeed: number = MAX_LAUNCH_SPEED,
) {
  let { vx, vy, vz } = plan;
  if (!Number.isFinite(vx) || !Number.isFinite(vy) || !Number.isFinite(vz)) {
    return;
  }
  // Safety clamp before write so nan-inf monitor never trips on bad √(2e/m).
  // Blast paths pass BLAST_MAX_LAUNCH so multi-charge volleys cannot stack past 8.
  const sp = Math.hypot(vx, vy, vz);
  if (sp > maxSpeed) {
    const s = maxSpeed / sp;
    vx *= s;
    vy *= s;
    vz *= s;
    rt.clampEngagements += 1;
  }
  if (sp < 1e-8) return;
  try {
    rt.world.setLinearVelocity(bodyId, [vx, vy, vz]);
    try {
      rt.world.wakeBody(bodyId);
    } catch {
      // ignore
    }
  } catch {
    // ignore
  }
}

/**
 * Apply damage-accumulation rim updates and pre-subdivide cells that reach
 * damage ≥ 1 (replaces radius-based preCrackRim).
 */
export function applyRimDamage(
  rt: DemolitionRuntime,
  result: ResolveResult,
  impact: DemolitionImpact,
  allowFallbackPreCrack: boolean = false,
) {
  for (const u of result.rimUpdates) {
    const rec = rt.records.get(u.cellId);
    if (!rec || rec.state !== 'static') continue;
    rec.damage += u.delta;
  }

  // Pre-crack cells whose damage crossed 1 (from this resolve or prior).
  const toCrack = new Set(result.preCrackCellIds);
  for (const rec of rt.records.values()) {
    if (
      rec.state === 'static' &&
      rec.damage >= 1 &&
      !rec.isSubChunk &&
      rec.preCrackGen === 0
    ) {
      toCrack.add(rec.cellId);
    }
  }

  // If the resolver flagged no pre-cracks but left rim deposits, force-crack the
  // highest-ratio statics (D2.1 e2e expects preCrackedCells > 0 after a bomb).
  if (allowFallbackPreCrack && toCrack.size === 0 && result.rimUpdates.length > 0) {
    const ranked = result.rimUpdates
      .slice()
      .sort((a, b) => b.sigmaRatio - a.sigmaRatio);
    for (const u of ranked.slice(0, 8)) {
      toCrack.add(u.cellId);
    }
  }

  // Last resort: any remaining static nearest the impact — guarantees a rim for
  // blast events when cascade consumed the energy-band rim entirely.
  if (allowFallbackPreCrack && toCrack.size === 0) {
    const statics: Array<{ id: number; d: number }> = [];
    for (const rec of rt.records.values()) {
      if (rec.state !== 'static' || rec.isSubChunk || rec.preCrackGen > 0) continue;
      const d = Math.hypot(
        rec.worldCentroid.x - impact.x,
        rec.worldCentroid.y - impact.y,
        rec.worldCentroid.z - impact.z,
      );
      statics.push({ id: rec.cellId, d });
    }
    statics.sort((a, b) => a.d - b.d);
    for (const s of statics.slice(0, 6)) toCrack.add(s.id);
  }

  if (toCrack.size === 0) return;

  reseedSubRng(rt, {
    x: impact.x + 0.17,
    y: impact.y,
    z: impact.z,
    speed: impact.speed,
  });

  let crackedParents = 0;
  for (const cellId of toCrack) {
    const cell = liveCellById(rt, cellId);
    const rec = rt.records.get(cellId);
    if (!cell || !rec || rec.state !== 'static' || rec.isSubChunk || rec.preCrackGen > 0) {
      continue;
    }
    if (wouldExceedLiveCap(rt, PRECRACK_PIECES - 1)) {
      noteLiveCapHit(rt);
      break;
    }

    const raw = subdivideCell(cell, PRECRACK_PIECES, rt.subRng);
    if (raw.length < 2) continue;

    try {
      destroyOwnedBody(rt, rec.bodyId as BodyHandle);
    } catch {
      // ignore
    }
    rt.staticBodyCount = Math.max(1, rt.staticBodyCount - 1);
    rt.records.delete(rec.cellId);

    const parentNeighbors = (rt.adjacency.get(cell.id) ?? []).slice();
    removeCellFromLiveRegistry(rt, cell.id);

    const subIds: number[] = [];
    for (const sub of raw) {
      const id = rt.nextCellId++;
      const owned: FractureCell = {
        ...sub,
        id,
        neighbors: [],
      };
      rt.cells.push(owned);
      rt.cellById.set(owned.id, owned);
      subIds.push(id);
      // A pre-crack divides the same weakened concrete; do not reset its
      // fatigue state simply because the collision proxy became finer.
      spawnCellBody(rt, owned, 'static', {
        isSubChunk: true,
        preCrackGen: 1,
        damage: Math.min(1, rec.damage * 0.6),
      });
    }

    for (const id of subIds) {
      const nbs = parentNeighbors.filter((n) => n !== cell.id);
      for (const sib of subIds) {
        if (sib !== id) nbs.push(sib);
      }
      rt.adjacency.set(id, nbs);
    }
    for (const n of parentNeighbors) {
      const list = rt.adjacency.get(n);
      if (!list) continue;
      const filtered = list.filter((x) => x !== cell.id);
      for (const id of subIds) {
        if (!filtered.includes(id)) filtered.push(id);
      }
      rt.adjacency.set(n, filtered);
    }

    crackedParents += 1;
  }

  if (crackedParents > 0) {
    rt.preCrackedCells += crackedParents;
    rt.idsDirty = true;
    rt.remnantDirty = true;
    recountCells(rt);
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(
        `[demolition] damage pre-crack parents=${crackedParents} total=${rt.preCrackedCells} ` +
          `live=${rt.cells.length}/${rt.maxLiveCells}`,
      );
    }
  }
}

/**
 * First topology-changing fracture of the intact monolith.
 * Resolves against the pristine partition BEFORE destroying the monolith.
 * Zero carve: accumulate fatigue; if an impact-timed partition was generated,
 * commit it as *latent* damage discretization (monolith kept, fractured=false)
 * so future hits reuse the same cell ids for pristineDamage.
 */
export function firstFracture(
  rt: DemolitionRuntime,
  impact: DemolitionImpact,
  event: DamageEvent,
  options: ResolveDamageOptions = {},
): CarveOutcome {
  const carveT0 = performance.now();
  const profile = rt.profile;
  const damageOrigin = { x: impact.x, y: impact.y, z: impact.z };

  // Impact-timed partition (glass spiderweb) — generate AT hit center for resolve.
  let impactPartition: FractureCell[] | null = null;
  let impactPartitionMs = 0;
  if (profile.partitionTiming === 'impact' && rt.pristineCells.length === 0) {
    const local: Vec3 = {
      x: impact.x - rt.wallCenter.x,
      y: impact.y - rt.wallCenter.y,
      z: impact.z - rt.wallCenter.z,
    };
    const built = buildImpactPartition(profile, local);
    impactPartition = built.cells;
    impactPartitionMs = built.ms;
  }

  const resolveCells =
    impactPartition ??
    (rt.pristineCells.length > 0 ? rt.pristineCells.slice() : rt.cells.slice());

  const localHit: Vec3 = {
    x: impact.x - rt.wallCenter.x,
    y: impact.y - rt.wallCenter.y,
    z: impact.z - rt.wallCenter.z,
  };
  const preferredPrimaryCellId = nearestCellSeedId(resolveCells, localHit);
  const damageBefore = damageBeforeAt(rt, impact, preferredPrimaryCellId);

  reseedSubRng(rt, impact);
  beginCarveComminution(rt);
  const comminutionCtx = comminutionContextForEvent(event);
  const result = resolveDamage(rt, event, {
    cells: resolveCells,
    preferredPrimaryCellId,
    launchScale: options.launchScale,
  });
  publishLastResolve(rt, result);

  const policy = decideFirstFractureCommit({
    carvedCellCount: result.carvedCellIds.length,
    hasImpactPartition: impactPartition != null,
  });

  if (!policy.topologyChanged) {
    // Subthreshold hit: keep monolith; accumulate fatigue on (possibly latent) ids.
    if (policy.commitPartition && impactPartition) {
      commitLatentPartition(rt, impactPartition, impactPartitionMs);
    }
    for (const u of result.rimUpdates) {
      const prev = rt.pristineDamage.get(u.cellId) ?? 0;
      rt.pristineDamage.set(u.cellId, prev + u.delta);
    }
    for (const d of result.deposits) {
      if (d.failed || d.energy <= 0) continue;
      if (result.rimUpdates.some((u) => u.cellId === d.cellId)) continue;
      const ratio = d.thresholdE > 0 ? d.energy / d.thresholdE : 0;
      if (ratio <= 0) continue;
      const delta = ratio * profile.physics.damageAccumRate * 0.25;
      if (delta <= 0) continue;
      const prev = rt.pristineDamage.get(d.cellId) ?? 0;
      rt.pristineDamage.set(d.cellId, prev + delta);
    }
    rt.lastCarveMs = performance.now() - carveT0;
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(
        `[demolition] firstFracture no-op (subthreshold) ` +
          `energy=${result.stats.energy.toFixed(0)}J fatigueCells=${rt.pristineDamage.size} ` +
          `latentPartition=${policy.commitPartition} material=${profile.id}`,
      );
    }
    const outcome = emptyCarveOutcome(damageOrigin, event.energy, preferredPrimaryCellId);
    commitCarveComminution(rt);
    recordDamageFact(rt, impact, event, outcome, result, damageBefore);
    return outcome;
  }

  // Topology-changing carve: commit impact partition (if any), destroy monolith.
  if (impactPartition) {
    commitLatentPartition(rt, impactPartition, impactPartitionMs);
  }

  if (rt.monolithBodyId) {
    try {
      destroyOwnedBody(rt, rt.monolithBodyId as BodyHandle);
    } catch {
      // ignore
    }
    rt.monolithBodyId = 0;
    rt.staticBodyCount = Math.max(1, rt.staticBodyCount - 1);
  }

  // Fatigue is now owned by live cell records (seeded below + applyRimDamage).
  const priorFatigue = new Map(rt.pristineDamage);
  rt.pristineDamage.clear();

  const baseSnapshot = rt.pristineCells.slice();
  setLiveCells(rt, baseSnapshot.slice());

  const hitIds = new Set(result.carvedCellIds);
  const subByParent = new Map(result.subPlans.map((p) => [p.cellId, p.extraPieces]));
  const impulseByCell = new Map(result.impulses.map((i) => [i.cellId, i]));

  type DynPlan =
    | { kind: 'whole'; cell: FractureCell }
    | { kind: 'subs'; parent: FractureCell; subs: FractureCell[] };
  const dynPlans: DynPlan[] = [];
  const budget = {
    remaining: subFractureBudgetForEvent(
      event.footprint.kind,
      profile.subFractureBudget,
      rt.volleySubBudget,
    ),
  };
  const subdividedParents = new Set<number>();

  // Process carved cells nearest-first (resolver already sorted).
  const carvedCells = result.carvedCellIds
    .map((id) => baseSnapshot.find((c) => c.id === id))
    .filter((c): c is FractureCell => !!c);

  // Capture original centroids before demotion (direct releases).
  const directReleases = releasesFromCellIds(rt, result.carvedCellIds, baseSnapshot, impulseByCell);
  const collapseBefore = new Set(rt.collapseQueue.map((j) => j.cellId));

  for (const cell of carvedCells) {
    let extra = subByParent.get(cell.id) ?? 0;
    // Framed stone beside the crater stays in the wall. Releasing it whole
    // lodges a mid-wall boulder; shattering it leaves pocket fragments at
    // the same height. Core cells (resolver extra > 0) still release.
    if (
      extra === 0 &&
      cell.volume > 0.12 &&
      profile.physics.brittleness >= 0.65
    ) {
      const wy = cell.centroid.y + rt.wallCenter.y;
      const lat = Math.hypot(
        cell.centroid.x + rt.wallCenter.x - impact.x,
        cell.centroid.z + rt.wallCenter.z - impact.z,
      );
      if (lat > FRAMED_BOULDER_LATERAL_M && wy > FRAMED_BOULDER_KEEP_Y) {
        hitIds.delete(cell.id);
        continue;
      }
    }
    // Large brittle cells always try multi-split (stone boulders → lower pile).
    if (
      cell.volume > 0.12 &&
      profile.physics.brittleness >= 0.65 &&
      budget.remaining >= 3
    ) {
      extra = Math.max(extra, 3);
    }
    const imp = impulseByCell.get(cell.id) ?? null;
    if (extra > 0 && budget.remaining > 0) {
      const subs = trySubdivideForCarve(
        rt,
        cell,
        impact,
        imp,
        budget,
        extra,
        false,
        comminutionCtx,
      );
      if (subs !== null) {
        subdividedParents.add(cell.id);
        setLiveCells(rt, rt.cells.filter((c) => c.id !== cell.id));
        if (subs.length === 0) continue;
        dynPlans.push({ kind: 'subs', parent: cell, subs });
        continue;
      }
    }
    const wc = worldCentroid(rt, cell);
    const wholeDist = Math.hypot(wc.x - impact.x, wc.y - impact.y, wc.z - impact.z);
    const wholeCrush = applyComminution({
      pieces: [{ volume: cell.volume, dist: wholeDist }],
      params: rt.profile.comminution,
      energyDensity: comminutionCtx.energyDensity,
      coreRadius: comminutionCtx.coreRadius,
      sizeFloorVol: rt.profile.sizeFloorVol,
    });
    if (wholeCrush.pulverizeIndices.length > 0) {
      setLiveCells(rt, rt.cells.filter((c) => c.id !== cell.id));
      rt.pendingPulverizedVolume += cell.volume;
      rt.removedChunks += 1;
      continue;
    }
    dynPlans.push({ kind: 'whole', cell });
  }

  dynPlans.sort((a, b) => {
    const va =
      a.kind === 'whole'
        ? a.cell.volume
        : a.subs.reduce((s, c) => s + c.volume, 0);
    const vb =
      b.kind === 'whole'
        ? b.cell.volume
        : b.subs.reduce((s, c) => s + c.volume, 0);
    return vb - va;
  });

  const dynamicBodies: BodyHandle[] = [];
  const bodyImpulse: Array<{ bodyId: BodyHandle; plan: { vx: number; vy: number; vz: number } }> =
    [];

  const spawnOne = (cell: FractureCell, isSub: boolean, parentId: number) => {
    const imp = impulseByCell.get(parentId) ?? impulseByCell.get(cell.id) ?? null;
    const h = spawnDynamicRespectingCap(rt, cell, {
      isSubChunk: isSub,
      impact,
      radius: 2.5,
      strength: imp ? Math.hypot(imp.vx, imp.vy, imp.vz) * 10 : 40,
    });
    if (h) {
      rt.pendingChunkVolume += cell.volume;
      dynamicBodies.push(h);
      if (imp) bodyImpulse.push({ bodyId: h, plan: imp });
    } else {
      rt.pendingShardVolume += cell.volume;
    }
  };

  for (const plan of dynPlans) {
    if (plan.kind === 'whole') {
      spawnOne(plan.cell, false, plan.cell.id);
    } else {
      const sorted = plan.subs.slice().sort((a, b) => b.volume - a.volume);
      for (const sub of sorted) spawnOne(sub, true, plan.parent.id);
    }
  }

  // Static remnant = base cells not carved. Seed prior pristine fatigue onto records.
  for (const cell of baseSnapshot) {
    if (hitIds.has(cell.id)) continue;
    if (subdividedParents.has(cell.id)) continue;
    const prior = priorFatigue.get(cell.id) ?? 0;
    spawnCellBody(rt, cell, 'static', prior > 0 ? { damage: prior } : {});
  }

  if (event.footprint.kind === 'sphere') {
    rt.volleySubBudget = budget.remaining;
  }

  rt.fractured = true;
  rt.wallVersion += 1;
  rt.idsDirty = true;
  rt.lastActivitySimTime = rt.stepper.simTime;
  rebuildBuffers(rt);

  // Apply per-chunk velocities from resolver (energy-derived).
  // Blast-carved chunks: cap at BLAST_MAX_LAUNCH (not the global 18 net).
  const blastLaunch =
    event.direction.kind === 'omni' || event.footprint.kind === 'sphere';
  for (const { bodyId, plan } of bodyImpulse) {
    applyImpulseToBody(rt, bodyId, plan, blastLaunch ? BLAST_MAX_LAUNCH : MAX_LAUNCH_SPEED);
  }
  clampLaunchSpeeds(rt, dynamicBodies, blastLaunch ? BLAST_MAX_LAUNCH : MAX_LAUNCH_SPEED);

  recountCells(rt);
  rt.remnantDirty = true;
  applyRimDamage(rt, result, impact, event.footprint.kind === 'sphere');
  runSupportCollapse(rt);
  refreshFragmentVolumes(rt);

  const supportReleases = supportReleasesSince(rt, collapseBefore, baseSnapshot, impulseByCell);

  rt.lastCarveMs = performance.now() - carveT0;
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(
      `[demolition] firstFracture carve ${rt.lastCarveMs.toFixed(2)}ms ` +
        `carved=${result.stats.carvedCells} surplus=${result.stats.surplusEnergy.toFixed(0)}J ` +
        `area=${result.stats.newSurfaceArea.toFixed(3)} sub=${result.stats.subPieces} ` +
        `dyn=${dynamicBodies.length} material=${profile.id}`,
    );
  }
  const outcome: CarveOutcome = {
    topologyChanged: true,
    carvedCellIds: result.carvedCellIds.slice(),
    preferredPrimaryCellId,
    damageOrigin,
    appliedEnergy: event.energy,
    directReleases,
    supportReleases,
  };
  // Direct carve releases can free an embedded spike; support queue demotions
  // un-embed later when processCollapseQueue actually demotes.
  unembedSpikesNearReleased(rt, releasePositionsFromSamples(directReleases));
  commitCarveComminution(rt);
  recordDamageFact(rt, impact, event, outcome, result, damageBefore);
  return outcome;
}

export function subsequentCarve(
  rt: DemolitionRuntime,
  impact: DemolitionImpact,
  event: DamageEvent,
  options: ResolveDamageOptions = {},
): CarveOutcome {
  const damageOrigin = { x: impact.x, y: impact.y, z: impact.z };
  const damageBefore = damageBeforeAt(rt, impact, options.preferredPrimaryCellId);
  // Glass / impact-timed: subsequent hits knock existing shards only (no re-partition).
  if (rt.profile.partitionTiming === 'impact') {
    const outcome = emptyCarveOutcome(damageOrigin, event.energy, options.preferredPrimaryCellId);
    recordDamageFact(rt, impact, event, outcome, null, damageBefore);
    return outcome;
  }

  const carveT0 = performance.now();
  reseedSubRng(rt, impact);
  beginCarveComminution(rt);
  const comminutionCtx = comminutionContextForEvent(event);
  const result = resolveDamage(rt, event, options);
  publishLastResolve(rt, result);

  if (result.carvedCellIds.length === 0) {
    // Fatigue / rim only — apply damage but do NOT claim topology change.
    // Returning topologyChanged=true here used to retire the projectile while
    // the wall stayed uncarved (projectile disappeared without a hole).
    if (result.rimUpdates.length > 0 || result.preCrackCellIds.length > 0) {
      applyRimDamage(rt, result, impact);
      refreshFragmentVolumes(rt);
    }
    rt.lastCarveMs = performance.now() - carveT0;
    const outcome = emptyCarveOutcome(
      damageOrigin,
      event.energy,
      options.preferredPrimaryCellId,
    );
    commitCarveComminution(rt);
    recordDamageFact(rt, impact, event, outcome, result, damageBefore);
    return outcome;
  }

  const cellLookup = rt.cells.slice();
  const subByParent = new Map(result.subPlans.map((p) => [p.cellId, p.extraPieces]));
  const impulseByCell = new Map(result.impulses.map((i) => [i.cellId, i]));
  const directReleases = releasesFromCellIds(rt, result.carvedCellIds, cellLookup, impulseByCell);
  const collapseBefore = new Set(rt.collapseQueue.map((j) => j.cellId));
  const budget = {
    remaining:
      options.subBudget ??
      (event.footprint.kind === 'sphere'
        ? subFractureBudgetForEvent(
            event.footprint.kind,
            rt.profile.subFractureBudget,
            rt.volleySubBudget,
          )
        : Math.min(rt.profile.subFractureBudget, SUBSEQUENT_IMPACT_SUB_BUDGET)),
  };

  type Pending =
    | { kind: 'demote'; rec: CellRecord; cell: FractureCell; vol: number; parentId: number }
    | {
        kind: 'subs';
        cell: FractureCell;
        rec: CellRecord;
        subs: FractureCell[];
        vol: number;
        parentId: number;
      };
  const pending: Pending[] = [];

  for (const cellId of result.carvedCellIds) {
    const cell = liveCellById(rt, cellId);
    const rec = rt.records.get(cellId);
    if (!cell || !rec || rec.state !== 'static') continue;

    const extra = subByParent.get(cellId) ?? 0;
    const imp = impulseByCell.get(cellId) ?? null;
    const wantSub = extra > 0 && budget.remaining > 0 && !rec.isSubChunk && rec.preCrackGen === 0;
    if (wantSub) {
      const subs = trySubdivideForCarve(
        rt,
        cell,
        impact,
        imp,
        budget,
        extra,
        true,
        comminutionCtx,
      );
      if (subs !== null) {
        try {
          destroyOwnedBody(rt, rec.bodyId as BodyHandle);
        } catch {
          // ignore
        }
        rt.staticBodyCount = Math.max(1, rt.staticBodyCount - 1);
        rt.records.delete(rec.cellId);
        removeCellFromLiveRegistry(rt, cell.id);
        if (subs.length === 0) continue;
        const vol = subs.reduce((s, c) => s + c.volume, 0);
        pending.push({ kind: 'subs', cell, rec, subs, vol, parentId: cellId });
        continue;
      }
    }
    const wc = worldCentroid(rt, cell);
    const wholeCrush = applyComminution({
      pieces: [{
        volume: cell.volume,
        dist: Math.hypot(wc.x - impact.x, wc.y - impact.y, wc.z - impact.z),
      }],
      params: rt.profile.comminution,
      energyDensity: comminutionCtx.energyDensity,
      coreRadius: comminutionCtx.coreRadius,
      sizeFloorVol: rt.profile.sizeFloorVol,
    });
    if (wholeCrush.pulverizeIndices.length > 0) {
      try {
        destroyOwnedBody(rt, rec.bodyId as BodyHandle);
      } catch {
        // ignore
      }
      rt.staticBodyCount = Math.max(1, rt.staticBodyCount - 1);
      rt.records.delete(rec.cellId);
      removeCellFromLiveRegistry(rt, cell.id);
      rt.pendingPulverizedVolume += cell.volume;
      rt.removedChunks += 1;
      continue;
    }
    pending.push({ kind: 'demote', rec, cell, vol: cell.volume, parentId: cellId });
  }

  pending.sort((a, b) => b.vol - a.vol);

  const newlyDynamic: BodyHandle[] = [];
  const bodyImpulse: Array<{ bodyId: BodyHandle; plan: { vx: number; vy: number; vz: number } }> =
    [];

  for (const p of pending) {
    const imp = impulseByCell.get(p.parentId) ?? null;
    if (p.kind === 'subs') {
      const sorted = p.subs.slice().sort((a, b) => b.volume - a.volume);
      for (const sub of sorted) {
        const h = spawnDynamicRespectingCap(rt, sub, {
          isSubChunk: true,
          impact,
          radius: 2.5,
          strength: imp ? Math.hypot(imp.vx, imp.vy, imp.vz) * 10 : 40,
        });
        if (h) {
          rt.pendingChunkVolume += sub.volume;
          newlyDynamic.push(h);
          if (imp) bodyImpulse.push({ bodyId: h, plan: imp });
        } else {
          rt.pendingShardVolume += sub.volume;
        }
      }
    } else if (countDynamics(rt) >= MAX_DYNAMIC_CHUNKS) {
      try {
        destroyOwnedBody(rt, p.rec.bodyId as BodyHandle);
      } catch {
        // ignore
      }
      rt.staticBodyCount = Math.max(1, rt.staticBodyCount - 1);
      rt.records.delete(p.rec.cellId);
      queueShardFromCellVel(rt, p.cell, impact, imp);
      rt.removedChunks += 1;
      rt.pendingShardVolume += p.cell.volume;
      removeCellFromLiveRegistry(rt, p.cell.id);
    } else {
      demoteToDynamic(rt, p.rec);
      const updated = rt.records.get(p.cell.id);
      if (updated) {
        rt.pendingChunkVolume += p.cell.volume;
        newlyDynamic.push(updated.bodyId as BodyHandle);
        if (imp) bodyImpulse.push({ bodyId: updated.bodyId as BodyHandle, plan: imp });
      }
    }
  }

  if (event.footprint.kind === 'sphere') {
    rt.volleySubBudget = budget.remaining;
  }

  if (newlyDynamic.length > 0) {
    rt.idsDirty = true;
    rt.lastActivitySimTime = rt.stepper.simTime;
    rebuildBuffers(rt);
    const blastLaunch =
      event.direction.kind === 'omni' || event.footprint.kind === 'sphere';
    const launchCap = blastLaunch ? BLAST_MAX_LAUNCH : MAX_LAUNCH_SPEED;
    for (const { bodyId, plan } of bodyImpulse) {
      applyImpulseToBody(rt, bodyId, plan, launchCap);
    }
    clampLaunchSpeeds(rt, newlyDynamic, launchCap);
  }

  recountCells(rt);
  rt.remnantDirty = true;
  applyRimDamage(rt, result, impact);
  runSupportCollapse(rt);
  refreshFragmentVolumes(rt);

  const supportReleases = supportReleasesSince(rt, collapseBefore, cellLookup, impulseByCell);

  rt.lastCarveMs = performance.now() - carveT0;
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(
      `[demolition] subsequentCarve ${rt.lastCarveMs.toFixed(2)}ms ` +
        `carved=${result.stats.carvedCells} newDyn=${newlyDynamic.length}`,
    );
  }
  const outcome: CarveOutcome = {
    topologyChanged: true,
    carvedCellIds: result.carvedCellIds.slice(),
    preferredPrimaryCellId: options.preferredPrimaryCellId,
    damageOrigin,
    appliedEnergy: event.energy,
    directReleases,
    supportReleases,
  };
  unembedSpikesNearReleased(rt, releasePositionsFromSamples(directReleases));
  commitCarveComminution(rt);
  recordDamageFact(rt, impact, event, outcome, result, damageBefore);
  return outcome;
}

export function demoteToDynamic(rt: DemolitionRuntime, rec: CellRecord) {
  if (rec.state !== 'static') return;
  // Static bodies were spawned with density 0 — setBodyType alone leaves mass 0.
  // Always destroy + respawn as a proper dynamic so gravity works.
  const cell = liveCellById(rt, rec.cellId);
  if (!cell) return;
  try {
    destroyOwnedBody(rt, rec.bodyId as BodyHandle);
  } catch {
    // ignore
  }
  rt.staticBodyCount = Math.max(1, rt.staticBodyCount - 1);
  rt.records.delete(rec.cellId);

  // F3: dyn-cap at spawn — collapse demotions also respect the hard cap.
  if (countDynamics(rt) >= MAX_DYNAMIC_CHUNKS) {
    const dummyImpact: DemolitionImpact = {
      x: rec.worldCentroid.x,
      y: rec.worldCentroid.y,
      z: rec.worldCentroid.z,
      speed: 8,
    };
    queueShardFromCell(rt, cell, dummyImpact, 1.0, 40);
    rt.removedChunks += 1;
    removeCellFromLiveRegistry(rt, cell.id);
    return;
  }
  spawnCellBody(rt, cell, 'dynamic', {
    isSubChunk: rec.isSubChunk,
    preCrackGen: rec.preCrackGen,
  });
}

// ---------------------------------------------------------------------------
// Support graph collapse
// ---------------------------------------------------------------------------

export function cellWorldCentroidY(rt: DemolitionRuntime, cellId: number): number {
  const rec = rt.records.get(cellId);
  if (rec) return rec.worldCentroid.y;
  const cell = liveCellById(rt, cellId);
  return cell ? cell.centroid.y + rt.wallCenter.y : 0;
}

export function runSupportCollapse(rt: DemolitionRuntime) {
  // Support graph: BFS from grounded remnant cells across ALL surviving static
  // adjacency (lateral + vertical). Only true disconnected islands collapse —
  // freestanding sections that still touch ground through any path stay put.
  const grounded: number[] = [];
  const staticIds: number[] = [];

  for (const cell of rt.cells) {
    const rec = rt.records.get(cell.id);
    if (!rec || rec.state !== 'static') continue;
    staticIds.push(cell.id);
    // AABB is in local (wall-centered) space; world min.y = aabb.min.y + wallCenter.y
    const worldMinY = cell.aabb.min.y + rt.wallCenter.y;
    if (worldMinY < GROUND_EPS) grounded.push(cell.id);
  }

  if (staticIds.length === 0) return;

  const staticSet = new Set(staticIds);
  const reached = new Set<number>();
  const queue = grounded.slice();
  for (const g of grounded) reached.add(g);

  while (queue.length > 0) {
    const id = queue.shift()!;
    const neighbors = rt.adjacency.get(id) ?? [];
    for (const n of neighbors) {
      if (!staticSet.has(n) || reached.has(n)) continue;
      reached.add(n);
      queue.push(n);
    }
  }

  const unsupported: number[] = [];
  for (const id of staticIds) {
    if (!reached.has(id)) unsupported.push(id);
  }

  if (unsupported.length === 0) return;

  // Avoid double-queuing cells already scheduled.
  const alreadyQueued = new Set(rt.collapseQueue.map((j) => j.cellId));

  // Stagger demotion over 0.15–0.35s with deterministic per-cell delay.
  // Prefer lower cells first so the ripple reads top-down after the base fails.
  unsupported.sort((a, b) => cellWorldCentroidY(rt, a) - cellWorldCentroidY(rt, b));
  const now = rt.fixedTime;
  for (let i = 0; i < unsupported.length; i += 1) {
    const id = unsupported[i];
    if (alreadyQueued.has(id)) continue;
    const delay = collapseDelayForCell(id, rt.wallVersion, i);
    rt.collapseQueue.push({ cellId: id, dueTime: now + delay });
  }
  rt.collapseQueue.sort((a, b) => a.dueTime - b.dueTime);
}

export function processCollapseQueue(rt: DemolitionRuntime) {
  if (rt.collapseQueue.length === 0) return;
  let dirty = false;
  const releasedCentroids: Array<{ x: number; y: number; z: number }> = [];
  while (rt.collapseQueue.length > 0 && rt.collapseQueue[0].dueTime <= rt.fixedTime) {
    const job = rt.collapseQueue.shift()!;
    const rec = rt.records.get(job.cellId);
    if (!rec || rec.state !== 'static') continue;
    releasedCentroids.push({
      x: rec.worldCentroid.x,
      y: rec.worldCentroid.y,
      z: rec.worldCentroid.z,
    });
    demoteToDynamic(rt, rec);
    rt.collapsedUnsupported += 1;
    dirty = true;
  }
  if (dirty) {
    unembedSpikesNearReleased(rt, releasedCentroids);
    rt.idsDirty = true;
    rt.lastActivitySimTime = rt.stepper.simTime;
    recountCells(rt);
    rt.remnantDirty = true;
  }
}
